// ⚠️ SÉCURITÉ : Les tokens JWT sont stockés dans localStorage.
// Solution recommandée pour la production : utiliser des cookies httpOnly
// (nécessite une modification côté backend pour les définir).
// localStorage est vulnérable au XSS — toujours sanitiser les entrées utilisateur.

const STORAGE = {
  get(key) {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  },
  set(key, val) {
    try {
      localStorage.setItem(key, val)
    } catch { /* stockage indisponible */ }
  },
  remove(key) {
    try {
      localStorage.removeItem(key)
    } catch { /* stockage indisponible */ }
  }
}

export default class AuthHelper {
  static getToken() {
    return STORAGE.get("token") || "";
  }

  static getRefreshToken() {
    return STORAGE.get("refresh_token") || "";
  }

  static getRefreshTokenExpire() {
    return STORAGE.get("refresh_token_expire") || "";
  }

  static getProfile() {
    try {
      const user = STORAGE.get("user");
      return user ? JSON.parse(user) : {};
    } catch (error) {
      return {};
    }
  }

  static getAccessType() {
    return STORAGE.get("access_type") || "U";
  }

  static getSuperAdmin() {
    return STORAGE.get("super_admin") === "true";
  }

  static getActiveOrganization() {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.organization || null;
    } catch {
      return null;
    }
  }

  static getPermissions() {
    return STORAGE.get("permissions") || "";
  }

  static isLogged() {
    const token = AuthHelper.getToken();
    const refreshToken = AuthHelper.getRefreshToken();
    const profile = AuthHelper.getProfile();

    return !!token && !!refreshToken && Object.keys(profile).length > 0;
  }

  static saveUserInfo(data) {
    if (data?.token) {
      STORAGE.set("token", data.token);
      try {
        const payload = JSON.parse(atob(data.token.split(".")[1]));
        if (payload.access_type) STORAGE.set("access_type", payload.access_type);
        if (payload.super_admin) STORAGE.set("super_admin", String(payload.super_admin));
      } catch {
        // JWT decode failure — ignore
      }
    }
    if (data?.access_type) {
      STORAGE.set("access_type", data.access_type);
    }
    if (data?.super_admin !== undefined) {
      STORAGE.set("super_admin", String(data.super_admin));
    }
    if (data?.refresh_token) {
      STORAGE.set("refresh_token", data.refresh_token);
    }
    if (data?.refresh_token_expire) {
      STORAGE.set("refresh_token_expire", data.refresh_token_expire);
    }
    if (data?.user) {
      STORAGE.set("user", JSON.stringify(data.user));
    }
    if (data?.permissions) {
      STORAGE.set("permissions", JSON.stringify(data.permissions));
    }
  }

  static logout() {
    STORAGE.remove("token");
    STORAGE.remove("refresh_token");
    STORAGE.remove("refresh_token_expire");
    STORAGE.remove("user");
    STORAGE.remove("permissions");
    STORAGE.remove("access_type");
    STORAGE.remove("super_admin");
  }

  static isTokenExpired() {
    const token = this.getToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000 < Date.now();
    } catch (error) {
      return true;
    }
  }

  // ============ NOUVELLE MÉTHODE ============
  /**
   * Vérifier si le refresh token est expiré
   * @returns {boolean} - true si expiré ou absent
   */
  static isRefreshTokenExpired() {
    const expireDate = this.getRefreshTokenExpire();

    // Si pas de date d'expiration, considérer comme expiré
    if (!expireDate) {
      console.log(" Pas de date d'expiration pour le refresh token");
      return true;
    }

    try {
      let expireTimestamp;

      // Si c'est un timestamp (nombre)
      if (!isNaN(expireDate)) {
        expireTimestamp = parseInt(expireDate);
      }
      // Si c'est une date string ISO
      else if (typeof expireDate === "string" && expireDate.includes("-")) {
        expireTimestamp = new Date(expireDate).getTime();
      }
      // Si c'est un nombre stocké comme string
      else {
        expireTimestamp = parseInt(expireDate);
      }

      // Vérifier si le timestamp est valide
      if (isNaN(expireTimestamp)) {
        console.log("Format de date invalide:", expireDate);
        return true;
      }

      const isExpired = expireTimestamp < Date.now();

      if (isExpired) {
        console.log(
          "Refresh token expiré le:",
          new Date(expireTimestamp).toLocaleString(),
        );
      } else {
        const daysLeft = Math.round(
          (expireTimestamp - Date.now()) / (1000 * 60 * 60 * 24),
        );
        console.log(`Refresh token valide - expire dans ${daysLeft} jours`);
      }

      return isExpired;
    } catch (error) {
      console.error(" Erreur vérification refresh token:", error);
      return true;
    }
  }

  /**
   * Récupérer le temps restant avant expiration du refresh token
   * @returns {Object|null} - { days, hours, minutes } ou null si pas de token
   */
  static getRefreshTokenTimeLeft() {
    const expireDate = this.getRefreshTokenExpire();
    if (!expireDate) return null;

    try {
      let expireTimestamp;

      if (!isNaN(expireDate)) {
        expireTimestamp = parseInt(expireDate);
      } else {
        expireTimestamp = new Date(expireDate).getTime();
      }

      const now = Date.now();
      const diffMs = expireTimestamp - now;

      if (diffMs <= 0) return null;

      return {
        days: Math.floor(diffMs / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)),
        milliseconds: diffMs,
      };
    } catch (error) {
      console.error("Erreur calcul temps restant:", error);
      return null;
    }
  }
}
